import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { canAnimate } from './motion';

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** How long a side panel takes to slide out before it is removed (design v3: 0.28s). */
export const PANEL_MS = 280;

/** Open side panels, last opened on top: only the top one handles Tab and Esc. */
const stack: object[] = [];

/**
 * Stops the page behind from scrolling without moving it: with a classic scrollbar, hiding it would widen
 * the page and shift everything sideways, so its width is kept as padding. Returns the undo.
 */
function lockPageScroll(): () => void {
  const root = document.documentElement;
  const body = document.body;
  // No layout (a test DOM): nothing to keep.
  const scrollbar = root.clientWidth > 0 ? window.innerWidth - root.clientWidth : 0;
  const before = { overflow: root.style.overflow, padding: body.style.paddingRight };
  root.style.overflow = 'hidden';
  if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
  return () => {
    root.style.overflow = before.overflow;
    body.style.paddingRight = before.padding;
  };
}

/**
 * Modal behaviour for a side panel (role="dialog"): focus moves in on open and back on close, Tab stays
 * inside, Esc closes, the page behind does not scroll. Panels stack (a member's drawer over a dashboard
 * detail): only the one on top reacts. A native <dialog> opened from a panel traps focus itself.
 */
export function useModalPanel(panel: RefObject<HTMLElement | null>, onClose: () => void): void {
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    const token = {};
    stack.push(token);
    const onTop = () => stack[stack.length - 1] === token;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // The panel slides in: focusing it must not scroll anything into view.
    panel.current?.focus({ preventScroll: true });
    const unlock = lockPageScroll();
    const trapTab = (e: KeyboardEvent) => {
      const el = panel.current;
      if (!el || document.querySelector('dialog[open]')) return;
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => !x.closest('dialog'));
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (!el.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (!onTop()) return;
      if (e.key === 'Tab') {
        trapTab(e);
        return;
      }
      if (e.key !== 'Escape' || document.querySelector('dialog[open]')) return;
      e.preventDefault();
      closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      stack.splice(stack.indexOf(token), 1);
      unlock();
      previous?.focus({ preventScroll: true });
    };
  }, [panel]);
}

/**
 * The design's slide for a side panel: it mounts off-screen, slides in once that first frame is painted
 * (so the work of building it never eats into the motion), and on close slides out before `onClosed` removes
 * it. `shown` drives the CSS; `close` is what every way out calls (✕, scrim, Esc, back). No motion when it is
 * reduced or the browser cannot tell.
 */
export function usePanelMotion(
  onClosed: () => void,
  onClosing?: () => void,
): { shown: boolean; close: () => void } {
  const [animate] = useState(canAnimate);
  const [shown, setShown] = useState(!animate);
  const closing = useRef(false);
  const done = useRef(onClosed);
  const leaving = useRef(onClosing);
  useEffect(() => {
    done.current = onClosed;
    leaving.current = onClosing;
  });

  useEffect(() => {
    if (!animate) return undefined;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        setShown(true);
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [animate]);

  // Gone before the slide ended (the URL changed under it): nothing left to close.
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    leaving.current?.();
    if (!animate) {
      done.current();
      return;
    }
    setShown(false);
    timer.current = setTimeout(() => {
      done.current();
    }, PANEL_MS);
  }, [animate]);

  return { shown, close };
}
